import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getUploadedFile } from "src/utils/storage";

export interface UploadedFile {
  /**
   * URL to preview: a local blob URL while a newly picked file is staged,
   * otherwise the download URL of the stored file.
   */
  url: string | undefined;
  /** The newly picked file, which the caller's save path still has to upload. */
  file: File | undefined;
  /** True when anything is attached, either stored or freshly picked. */
  hasFile: boolean;
  /** True while a stored file's download URL is still being fetched. */
  loading: boolean;
  setFile: (file?: File) => void;
  clear: () => void;
}

/**
 * Preview state for a single attached file: the URL to show plus the file the
 * user just picked. Resolving `filename` to a download URL is async. A newly
 * picked file previews from a local blob URL created in `setFile`.
 *
 * `label` only names the thing in the fetch error toast.
 *
 * `filename` becoming `undefined` deliberately doesn't clear the preview, since
 * a staged file has no stored filename yet. Removing an attachment means
 * calling `clear()` alongside whatever clears the stored filename.
 */
export function useUploadedFile(
  filename: string | undefined,
  { label = "file" }: { label?: string } = {}
): UploadedFile {
  const [file, setFileState] = useState<File | undefined>(undefined);
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [settledFilename, setSettledFilename] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (!filename) return;
    const controller = new AbortController();
    (async () => {
      try {
        const uploadedUrl = await getUploadedFile(filename);
        if (controller.signal.aborted) return;
        if (uploadedUrl) setUrl(uploadedUrl);
      } catch (error) {
        if (controller.signal.aborted) return;
        toast.error(
          `There seemed to be an error fetching the ${label}: ${
            (error as Error).message
          }`
        );
      }
      setSettledFilename(filename);
    })();
    return () => controller.abort();
  }, [filename, label]);

  const setFile = useCallback((next?: File) => {
    setFileState(next);
    if (next) setUrl(URL.createObjectURL(next));
  }, []);

  const clear = useCallback(() => {
    setUrl(undefined);
    setFileState(undefined);
  }, []);

  return {
    url,
    file,
    hasFile: !!(url || file),
    loading: !!filename && !file && settledFilename !== filename,
    setFile,
    clear,
  };
}
